/* =====================================================================
   tower/driver/src/confirm.js — the handheld's ONE confirmation sheet (E16, Parik 2026-10-22: "ask for all major
   confirmations when ending, deleting, changing something on driver app").

     drvConfirm({ title, body, confirm: { label, tone: 'danger' | 'primary' }, cancel: { label } }) → Promise<boolean>

   `body` is a string or a list of strings (one paragraph each) and must say exactly what will happen — the customer,
   the counts, the consequence. Every string is plain text (escaped here).

   What it guarantees, for every caller:
     · an in-app sheet in the design system (all four layouts, all ten themes) — never window.confirm/alert, which block
       automation and look foreign on the phone;
     · the SAFE option is first and has the focus (Enter on a fresh sheet goes back); the confirm button is labelled with
       what it does, and red-toned when it is destructive;
     · Escape, a tap outside the sheet, and the phone's Back button are "Go back" (false); focus stays inside the sheet;
     · local only — it works offline and sends nothing itself;
     · one at a time: a second question answers the first with "Go back".
   Every visible sheet owns one history entry (a sheet that replaces another takes it over), and the screen behind a
   question is inert. drvSheetGuard() gives a reason sheet that IS the confirmation (Skip, Close remaining) the same
   Escape / Back = Cancel, its consequence as aria-describedby, and action names on its reason buttons.
   Routine taps (adding a product, a quantity, navigation) are never confirmed — confirmation fatigue is a real cost.
   ===================================================================== */
import { t } from './i18n.js';
import { esc } from './ui.js';

const ID = 'drvConfirm';
let _open = null;          // the confirmation: { resolve, prevFocus, el, hist, popped }
let _guard = null;         // a reason sheet that is itself the confirmation: { el, cancel, mo, hist, popped }
let _skipPop = 0;          // our own history.back() calls, whose popstate is not a Back press
let _inerted = [];         // what we made inert behind the confirmation

/* ── the phone's Back button ──────────────────────────────────────────────────────────────────
   Every visible sheet owns ONE history entry, so Back closes it as "Go back" instead of leaving the app. A sheet that
   replaces another (a double tap, a language switch) takes over the entry (replaceState) — it never goes without one. */
const _mark = () => Object.assign({}, history.state || {}, { drvConfirm: 1 });
const _ours = () => !!(history.state && history.state.drvConfirm);
function _enter(layer, replacing) {
  try {
    if (replacing && _ours()) history.replaceState(_mark(), '');
    else history.pushState(_mark(), '');
    layer.hist = true;
  } catch (e) {}
}
function _leave(layer) {
  if (!layer.hist || layer.popped) return;
  try { if (_ours()) { _skipPop++; history.back(); } } catch (e) {}
}

function _inert(on, keep) {
  if (on) { [...document.body.children].forEach((x) => { if (x !== keep && !x.inert && x.tagName !== 'SCRIPT') { x.inert = true; _inerted.push(x); } }); return; }
  _inerted.forEach((x) => { try { x.inert = false; } catch (e) {} }); _inerted = [];
}

function _finish(answer, replacing) {
  const o = _open; if (!o) return;
  _open = null;
  try { o.mo.disconnect(); } catch (e) {}
  try { o.el.remove(); } catch (e) {}
  if (!replacing) {
    if (!_guard) document.removeEventListener('keydown', _onKey, true);
    _inert(false);
    _leave(o);
    try { if (o.prevFocus && document.contains(o.prevFocus)) o.prevFocus.focus({ preventScroll: true }); } catch (e) {}
  }
  o.resolve(!!answer);
}

function _onKey(e) {
  if (e.key === 'Escape' || e.key === 'Esc') {
    if (_open) { e.preventDefault(); e.stopPropagation(); _finish(false); return; }
    if (_guard) { e.preventDefault(); e.stopPropagation(); _guard.cancel(); return; }
  }
  if (_open && e.key === 'Tab') {   // focus stays inside the sheet
    const f = [..._open.el.querySelectorAll('button:not([disabled])')]; if (!f.length) return;
    const i = f.indexOf(document.activeElement);
    const next = e.shiftKey ? (i <= 0 ? f[f.length - 1] : f[i - 1]) : (i < 0 || i === f.length - 1 ? f[0] : f[i + 1]);
    e.preventDefault(); next.focus();
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    if (_skipPop > 0) {
      _skipPop--;
      // our own back() landed after a new sheet opened (a quick second question): give the visible sheet its entry again
      const top = _open || _guard; if (top && !_ours()) { try { history.pushState(_mark(), ''); top.hist = true; } catch (e) {} }
      return;
    }
    if (_open) { _open.popped = true; _finish(false); return; }    // the phone's Back button
    if (_guard) { _guard.popped = true; _guard.cancel(); }
  });
}

/** Ask. Resolves true only when the driver presses the confirm button. */
export function drvConfirm(o) {
  o = o || {};
  const replacing = !!_open, prev = _open;
  if (prev) _finish(false, true);   // one at a time: the earlier question is answered "Go back"; this sheet takes its place
  const c = o.confirm || {}, k = o.cancel || {};
  const danger = c.tone === 'danger';
  const paras = (Array.isArray(o.body) ? o.body : [o.body]).filter((x) => x != null && String(x).trim() !== '');
  const el = document.createElement('div');
  // i18n-exempt: class names, not words
  el.className = 'dh-sheetwrap dh-cfwrap'; el.id = ID;
  el.innerHTML = `<div class="dh-sheet dh-cf" role="alertdialog" aria-modal="true" aria-labelledby="drvCfT"${paras.length ? ' aria-describedby="drvCfB"' : ''}>
    <div class="dh-grip" aria-hidden="true"></div>
    <div class="dh-sheet-h"><b id="drvCfT">${esc(o.title || '')}</b></div>
    ${paras.length ? `<div class="dh-sheet-b dh-cf-b" id="drvCfB">${paras.map((p) => `<p class="dh-cf-p">${esc(p)}</p>`).join('')}</div>` : ''}
    <div class="dh-bar dh-cf-bar">
      <button type="button" class="dh-sec dh-cf-no" data-act="cfNo" onclick="drvConfirmAnswer(false)"><span class="dh-bl">${esc(k.label || t('dh.cf.back'))}</span></button>
      <button type="button" class="dh-pri dh-cf-yes${danger ? ' dh-danger' : ''}" data-act="cfYes" onclick="drvConfirmAnswer(true)"><span class="dh-bl">${esc(c.label || t('dh.cf.yes'))}</span></button>
    </div></div>`;
  el.addEventListener('click', (e) => { if (e.target === el) _finish(false); });   // a tap outside the sheet
  return new Promise((resolve) => {
    _open = { resolve, el, prevFocus: prev ? prev.prevFocus : document.activeElement, hist: prev ? prev.hist : false, popped: false, mo: null };
    document.body.appendChild(el);
    // taken off the page by anything else (a cleanup, a re-render of the body): answered "Go back", nothing left inert
    const me = _open; me.mo = new MutationObserver(() => { if (_open === me && !document.contains(el)) _finish(false); });
    me.mo.observe(document.body, { childList: true });
    document.addEventListener('keydown', _onKey, true);   // the same listener is added once only
    _inert(true, el);                  // the screen behind cannot be reached while the question is open
    _enter(_open, replacing);
    const no = el.querySelector('[data-act="cfNo"]'); try { no.focus({ preventScroll: true }); } catch (e) {}
  });
}

/** A reason sheet that is itself the confirmation (Skip, Close remaining): Escape and the phone's Back are its Cancel;
    it is described by its consequence line (aria-describedby), and each reason button's accessible name says what it
    does (`o.aria(label)` → "Skip — Customer closed"). `cancel` closes it. The guard ends when the sheet leaves the page. */
let _gseq = 0;
export function drvSheetGuard(el, cancel, o) {
  if (!el) return el;
  o = o || {};
  const replacing = !!_guard, prev = _guard;
  if (prev) { try { prev.mo.disconnect(); } catch (e) {} }
  const sh = el.querySelector('.dh-sheet'), sub = el.querySelector('.dh-sheet-h .dh-sub');
  if (sh) { sh.setAttribute('aria-modal', 'true');
    if (sub) { sub.id = sub.id || ('drvShD' + (++_gseq)); sh.setAttribute('aria-describedby', sub.id); } }
  if (o.rows && o.aria) el.querySelectorAll(o.rows).forEach((b) => { const l = (b.textContent || '').trim(); if (l) b.setAttribute('aria-label', o.aria(l)); });
  const g = { el, cancel, hist: prev ? prev.hist : false, popped: false, mo: null };
  g.mo = new MutationObserver(() => { if (!document.contains(el)) _unguard(g); });
  g.mo.observe(document.body, { childList: true });
  _guard = g;
  if (!replacing) document.addEventListener('keydown', _onKey, true);
  _enter(g, replacing);
  return el;
}
function _unguard(g) {
  if (_guard !== g) return;
  _guard = null;
  try { g.mo.disconnect(); } catch (e) {}
  if (!_open) document.removeEventListener('keydown', _onKey, true);
  _leave(g);
}

/** The two buttons' handler (an attribute, so every action names a handler that exists — check-driver-layouts). */
export function drvConfirmAnswer(yes) { _finish(!!yes); }
/** Is a confirmation open? (for a re-render that must not paint over it) */
export const drvConfirmOpen = () => !!_open;

if (typeof window !== 'undefined') Object.assign(window, { drvConfirm, drvConfirmAnswer, drvConfirmOpen, drvSheetGuard });
